import { useState, useMemo, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Search,
  ChevronRight,
  Package,
  Download,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { BoBCard, BoBCardSkeleton } from "@/components/BoBCard";
import { BoBDetailModal } from "@/components/BoBDetailModal";
import { BoBInstallProgress } from "@/components/BoBInstallProgress";

interface BoB {
  id: string;
  name: string;
  version: string;
  description: string;
  category: string;
  author: string;
  size_compressed: number;
  metadata?: {
    requires_connector?: string;
    requires_odoo?: string;
    changelog_url?: string;
    dependencies?: string[];
  };
}

// Custom hook for debouncing search
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function BoBMarketplace() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [selectedBoBs, setSelectedBoBs] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [selectedBoB, setSelectedBoB] = useState<BoB | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showInstallProgress, setShowInstallProgress] = useState(false);

  // Debounce search query
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Fetch available BoBs
  const { data, isLoading, error } = useQuery({
    queryKey: ["bobs", "available", categoryFilter, debouncedSearch],
    queryFn: () =>
      api.getAvailableBoBs({
        category: categoryFilter === "all" ? null : categoryFilter,
        search: debouncedSearch,
      }),
  });

  const bobs: BoB[] = data?.bobs || [];

  // Fetch installed BoBs for the instance
  const { data: installedBoBsData } = useQuery({
    queryKey: ["bobs", "installed", instanceId],
    queryFn: () => {
      if (!instanceId) throw new Error("Instance ID is required");
      return api.getInstanceBoBs(instanceId);
    },
    enabled: !!instanceId,
  });

  // Create a set of installed BoB names for quick lookup
  const installedBoBNames = useMemo(() => {
    if (!installedBoBsData?.bobs) return new Set<string>();
    return new Set(installedBoBsData.bobs.map((bob: any) => bob.name));
  }, [installedBoBsData]);

  // Install mutation
  const installMutation = useMutation({
    mutationFn: (bobNames: string[]) => {
      if (!instanceId) throw new Error("Instance ID is required");
      return api.batchInstallBoBs(instanceId, bobNames);
    },
    onSuccess: () => {
      setShowInstallProgress(true);
    },
  });

  const handleToggleSelect = (bobName: string) => {
    // Don't allow selecting already installed BoBs
    if (installedBoBNames.has(bobName)) {
      return;
    }

    setSelectedBoBs((prev) => {
      const next = new Set(prev);
      if (next.has(bobName)) {
        next.delete(bobName);
      } else {
        next.add(bobName);
      }
      return next;
    });
  };

  const handleViewDetails = (bob: BoB) => {
    setSelectedBoB(bob);
    setShowDetailModal(true);
  };

  const handleInstallFromModal = () => {
    if (selectedBoB) {
      setSelectedBoBs(new Set([selectedBoB.name]));
      setShowDetailModal(false);
      installMutation.mutate([selectedBoB.name]);
    }
  };

  const handleInstallSelected = () => {
    installMutation.mutate(Array.from(selectedBoBs));
  };

  const handleClearSelection = () => {
    setSelectedBoBs(new Set());
  };

  const handleRetryInstallation = (bobName: string) => {
    // Re-trigger installation for a single BoB
    installMutation.mutate([bobName]);
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4" role="alert">
        <div className="text-red-400">Error loading BoB marketplace</div>
        <div className="text-sm text-slate-500">
          {(error as Error).message || "An error occurred"}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <header>
        <div className="flex items-center gap-2 text-sm text-slate-300 mb-2">
          <Link
            to={`/client/instances/${instanceId}`}
            className="hover:text-slate-300 transition-colors"
          >
            Back to Instance
          </Link>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
          <span className="text-slate-300">BoB Marketplace</span>
        </div>
        <h1 className="text-3xl font-semibold text-slate-100">
          BoB Marketplace
        </h1>
        <p className="text-sm text-slate-300 mt-2">
          Browse and install Business-oriented Bots to extend your Odoo
          functionality
        </p>
      </header>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="flex-1" role="search">
          <label htmlFor="bob-search" className="sr-only">
            Search BoBs
          </label>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300"
              aria-hidden="true"
            />
            <Input
              id="bob-search"
              type="search"
              placeholder="Search BoBs by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              aria-label="Search BoBs"
            />
          </div>
        </div>

        {/* Category filter */}
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger
            className="w-full sm:w-[200px]"
            aria-label="Filter by category"
          >
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="finance">Finance</SelectItem>
            <SelectItem value="hr">Human Resources</SelectItem>
            <SelectItem value="sales">Sales</SelectItem>
            <SelectItem value="inventory">Inventory</SelectItem>
            <SelectItem value="productivity">Productivity</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Selected BoBs summary */}
      {selectedBoBs.size > 0 && (
        <Card className="bg-blue-500/10 border-blue-500/20">
          <CardContent className="flex items-center justify-between p-4">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-400" aria-hidden="true" />
              <span className="font-medium text-slate-100">
                {selectedBoBs.size} BoB{selectedBoBs.size !== 1 ? "s" : ""}{" "}
                selected
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearSelection}
                aria-label="Clear selection"
              >
                Clear
              </Button>
              <Button
                size="sm"
                onClick={handleInstallSelected}
                disabled={installMutation.isPending}
                aria-label={`Install ${selectedBoBs.size} selected BoB${selectedBoBs.size !== 1 ? "s" : ""}`}
              >
                {installMutation.isPending ? (
                  <>
                    <Loader2
                      className="h-4 w-4 mr-2 animate-spin"
                      aria-hidden="true"
                    />
                    Installing...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" aria-hidden="true" />
                    Install Selected
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* BoB grid */}
      {isLoading ? (
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          role="list"
          aria-label="Loading BoBs"
        >
          {[...Array(6)].map((_, i) => (
            <BoBCardSkeleton key={i} />
          ))}
        </div>
      ) : bobs.length === 0 ? (
        <div className="space-y-6">
          {/* PROD-P0-004: Enhanced empty state with helpful guidance */}
          <Card className="p-12 text-center bg-slate-800/30 border-slate-700">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-500/10 mb-4">
              <Package
                className="h-8 w-8 text-purple-400"
                aria-hidden="true"
              />
            </div>
            <h2 className="text-xl font-semibold text-slate-100 mb-2">
              {searchQuery || categoryFilter !== "all"
                ? "No BoBs Match Your Filters"
                : "No BoBs Available Yet"}
            </h2>
            <p className="text-sm text-slate-300 mb-6 max-w-md mx-auto">
              {searchQuery || categoryFilter !== "all" ? (
                <>
                  Try adjusting your search terms or category filters to find more BoBs.
                  <br />
                  You can also clear all filters to see the complete catalog.
                </>
              ) : (
                "The BoB marketplace is currently being populated. Check back soon for Business-oriented Bots that will supercharge your Odoo workflows."
              )}
            </p>
            {(searchQuery || categoryFilter !== "all") && (
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery("");
                    setCategoryFilter("all");
                  }}
                  aria-label="Clear all filters"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </Card>

          {/* Help section for empty state */}
          {!searchQuery && categoryFilter === "all" && (
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-4">
                What are Business-oriented Bots (BoBs)?
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <Card className="p-6 bg-slate-800/30 border-slate-700">
                  <h4 className="font-semibold text-slate-100 mb-2 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-500/10 text-blue-400 text-xs">✓</span>
                    Workflow Automation
                  </h4>
                  <p className="text-sm text-slate-300">
                    BoBs automate repetitive tasks in your Odoo instance, from invoice processing to inventory management.
                  </p>
                </Card>
                <Card className="p-6 bg-slate-800/30 border-slate-700">
                  <h4 className="font-semibold text-slate-100 mb-2 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-emerald-500/10 text-emerald-400 text-xs">✓</span>
                    AI-Powered Intelligence
                  </h4>
                  <p className="text-sm text-slate-300">
                    Each BoB uses AI to make intelligent decisions, learn from patterns, and improve over time.
                  </p>
                </Card>
                <Card className="p-6 bg-slate-800/30 border-slate-700">
                  <h4 className="font-semibold text-slate-100 mb-2 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-purple-500/10 text-purple-400 text-xs">✓</span>
                    Easy Installation
                  </h4>
                  <p className="text-sm text-slate-300">
                    Install BoBs with a single click - they integrate seamlessly with your existing Odoo workflows.
                  </p>
                </Card>
                <Card className="p-6 bg-slate-800/30 border-slate-700">
                  <h4 className="font-semibold text-slate-100 mb-2 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-yellow-500/10 text-yellow-400 text-xs">✓</span>
                    Customizable
                  </h4>
                  <p className="text-sm text-slate-300">
                    Configure each BoB to match your specific business needs and workflows.
                  </p>
                </Card>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            role="list"
            aria-label="Available BoBs"
          >
            {bobs.map((bob) => (
              <BoBCard
                key={bob.id}
                bob={bob}
                isSelected={selectedBoBs.has(bob.name)}
                isInstalled={installedBoBNames.has(bob.name)}
                onToggleSelect={() => handleToggleSelect(bob.name)}
                onViewDetails={() => handleViewDetails(bob)}
              />
            ))}
          </div>

          {/* Results count */}
          <div
            className="text-sm text-slate-300 text-center"
            role="status"
            aria-live="polite"
          >
            Showing {bobs.length} BoB{bobs.length !== 1 ? "s" : ""}
          </div>
        </>
      )}

      {/* Detail modal */}
      {selectedBoB && (
        <BoBDetailModal
          bob={selectedBoB}
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          onInstall={handleInstallFromModal}
        />
      )}

      {/* Installation progress modal */}
      {showInstallProgress && instanceId && (
        <BoBInstallProgress
          instanceId={instanceId}
          onClose={() => setShowInstallProgress(false)}
          onRetry={handleRetryInstallation}
        />
      )}
    </div>
  );
}
